/**
 * Peer Service
 * Unified peer interactions: discovery, CRUD, config, messaging
 */

import { createChatEngine } from './chat-engine.js'
import { canHost, mergePeers } from './peer.js'

/**
 * Select the default host peer from a list of peers.
 * Preference order:
 *  1. An LLM bot with roleType === 'host' (local LLM path)
 *  2. An online worker that canHost() — e.g. Claude Code worker (roleType === 'chat')
 * Returns null when no host-capable peer is available.
 *
 * Invariant: the worker-fallback predicate must stay in sync with canHost() in peer.ts.
 * Do not inline a narrower condition here (e.g. `type === 'llm-bot'` guards) — that
 * reintroduces the regression where new chat-worker types become unroutable.
 */
export function resolveDefaultHost(peers: any[]) {
  const llmBotHost = peers.find((p: any) => p.type === 'llm-bot' && p.roleType === 'host')
  if (llmBotHost) return llmBotHost
  return peers.find((p: any) => canHost(p) && p.online && p.type !== 'llm-bot') || null
}

export function createPeerService({
  botConfigService,
  discoverRoles,
  roleCall,
  deleteRole,
  excludeBotIds,
  // Chat engine deps -- required for handleUserMessage
  chatFn,
  buildPrompt,
}: any) {
  let cachedPeers: any[] = []
  let cachedBots: any[] = []

  const discover = async (opts?: any) => {
    const [bots, roles] = await Promise.all([botConfigService.getBots(), discoverRoles(opts)])
    cachedBots = bots
    cachedPeers = mergePeers(bots, roles, excludeBotIds)
    return { peers: cachedPeers, bots, roles }
  }

  const getPeer = (roleId: string) => cachedPeers.find((p: any) => p.roleId === roleId) || null

  const getBot = (botId: string) => cachedBots.find((b: any) => b.botId === botId) || null

  const addBot = (config: any) => botConfigService.createBot(config)

  const updateBot = (botId: string, updates: any) => botConfigService.updateBot(botId, updates)

  const removePeer = async (roleId: string) => {
    const peer = getPeer(roleId)
    if (peer?.type === 'llm-bot') {
      await botConfigService.deleteBot(roleId)
    } else {
      await deleteRole(roleId)
    }
  }

  const getWorkerConfig = (roleId: string) => roleCall(roleId, 'getConfig', [])

  const updateWorkerConfig = (roleId: string, values: any) =>
    roleCall(roleId, 'updateConfig', [values])

  const chatEngine = chatFn ? createChatEngine({ chatFn, buildPrompt, roleCall }) : null

  return {
    discover,
    getPeer,
    getBot,
    addBot,
    updateBot,
    removePeer,
    getWorkerConfig,
    updateWorkerConfig,
    chatEngine,
  }
}
